import { createContext, FC, useContext, useMemo, useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { useCreateChatClient } from 'stream-chat-react';
import { jwtDecode } from 'jwt-decode';

import { StreamChatGenerics } from '../modules/dashboard-modules/inbox/stream-chat-types';
import { GET_STREAM_DATA_KEY } from '../constants/config';
import { IGetStreamData } from '../api/services/auth/types';
import { Children } from '../constants/types/prop.types';

interface IChatClientContext {
  chatClient: ReturnType<typeof useCreateChatClient<StreamChatGenerics>> | null;
  disconnectChat: () => void;
  isInitializing: boolean;
}

const ChatClientContext = createContext<IChatClientContext>({
  chatClient: null,
  disconnectChat: () => {},
  isInitializing: false,
});

const ChatClientProvider: FC<Children> = ({ children }) => {
  const [getStreamData, setGetStreamData] = useLocalStorage<IGetStreamData>(GET_STREAM_DATA_KEY, {
    token: '',
    apiKey: '',
  });
  const [isInitializing, setIsInitializing] = useState(true);

  // Only try to create the client when we have all required data

  const decodedObject: { user_id: string } | null = getStreamData?.token
    ? jwtDecode(getStreamData?.token)
    : null;

  const userId = decodedObject?.user_id || '';

  const hasValidData = !!getStreamData?.token && !!getStreamData?.apiKey && !!userId;

  // Create chat client with validation
  const chatClientResult = useCreateChatClient<StreamChatGenerics>({
    apiKey: getStreamData?.apiKey || '',
    tokenOrProvider: getStreamData?.token || '',
    userData: { id: userId },
  });

  // Only use the chat client if data is valid
  const chatClient = hasValidData ? chatClientResult : null;

  // Initialize the chat client when data becomes available
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (hasValidData) {
      setIsInitializing(true);

      // Add a small delay to ensure all data is properly synchronized
      timeoutId = setTimeout(() => {
        setIsInitializing(false);
      }, 500);
    } else {
      setIsInitializing(false);
    }

    // Clean up timeout on unmount or when dependencies change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [hasValidData]);

  // Clean up any existing connections on unmount
  useEffect(
    () => () => {
      if (chatClient) {
        chatClient.disconnectUser();
      }
    },
    [chatClient],
  );

  // Ensure connection is closed when user data changes
  useEffect(() => {
    if (chatClient && !hasValidData) {
      chatClient.disconnectUser();
    }
  }, [chatClient, hasValidData]);

  const disconnectChat = useCallback(() => {
    if (chatClient) {
      chatClient.disconnectUser();
      setGetStreamData({ token: '', apiKey: '' });
    }
  }, [chatClient]);

  const memoizedValue = useMemo(
    () => ({
      chatClient,
      disconnectChat,
      isInitializing,
    }),
    [chatClient, disconnectChat, isInitializing],
  );

  return <ChatClientContext.Provider value={memoizedValue}>{children}</ChatClientContext.Provider>;
};

export default ChatClientProvider;

export const useChatClient = () => useContext(ChatClientContext);
